import React, { useState } from "react";
import "../styles/StudentRegistration.css";
import { isFieldAlreadyRegistered } from "../api/studentApi";
import { createRegistrationOrder } from "../api/paymentApi";
import { processRazorpayPayment } from "../utils/razorpayCheckout";
import {
  StudentRegistrationData,
  StudentRegistrationFormErrors,
} from "../types";

const MONTHLY_AMOUNT = 500;
const DAYS_PER_MONTH = 31;
const MIN_AMOUNT = 200;

const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value?: string | null): string => {
  if (!value) return "-";
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const addDays = (dateStr: string, days: number): string => {
  const [year, month, day] = dateStr.slice(0, 10).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const calculateGrantedDays = (amount: number): number => {
  if (!amount || amount <= MIN_AMOUNT) {
    return 0;
  }
  return Math.floor((amount * DAYS_PER_MONTH) / MONTHLY_AMOUNT);
};

const emptyForm = (joiningDate: string): StudentRegistrationData => ({
  firstName: "",
  lastName: "",
  emailId: "",
  aadharNumber: "",
  optionalIdentification: "",
  mobileNumber: "",
  alternateNumber: "",
  guardianName: "",
  guardianMobileNumber: "",
  genderCode: "",
  address: "",
  joiningDate,
  amount: "500",
  photo: null,
});

const StudentRegistration: React.FC = () => {
  const [formData, setFormData] = useState<StudentRegistrationData>(() =>
    emptyForm(getTodayDate())
  );

  const [errors, setErrors] = useState<StudentRegistrationFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  const validateForm = async (): Promise<boolean> => {
    const newErrors: StudentRegistrationFormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.emailId.trim()) {
      newErrors.emailId = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      newErrors.emailId = "Please enter a valid email address";
    } else if (await isFieldAlreadyRegistered("EmailId", formData.emailId)) {
      newErrors.emailId = "This email is already registered";
    }

    if (!formData.aadharNumber.trim()) {
      newErrors.aadharNumber = "Aadhar Number is required";
    } else if (!/^\d{12}$/.test(formData.aadharNumber.replace(/\s/g, ""))) {
      newErrors.aadharNumber = "Please enter a valid 12-digit Aadhar number";
    } else if (
      await isFieldAlreadyRegistered(
        "AadharNumber",
        formData.aadharNumber.replace(/\s/g, "")
      )
    ) {
      newErrors.aadharNumber = "This Aadhar Number is already registered";
    }

    if (!formData.genderCode.trim()) {
      newErrors.genderCode = "Gender is required";
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (formData.mobileNumber.trim().startsWith("+91")) {
      newErrors.mobileNumber =
        "Please do not add country code (+91). Enter 10-digit mobile number only";
    } else if (!/^[6-9]\d{9}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Please enter a valid 10-digit mobile number";
    } else if (
      await isFieldAlreadyRegistered("MobileNumber", formData.mobileNumber)
    ) {
      newErrors.mobileNumber = "This Mobile Number is already registered";
    }

    if (
      formData.alternateNumber.trim() &&
      !/^\+?[\d\s-()]{7,15}$/.test(formData.alternateNumber)
    ) {
      newErrors.alternateNumber =
        "Please enter a valid alternate mobile number";
    } else if (formData.alternateNumber.trim().startsWith("+91")) {
      newErrors.alternateNumber =
        "Please do not add country code (+91). Enter 10-digit mobile number only";
    }

    if (!formData.guardianName.trim()) {
      newErrors.guardianName = "Guardian name is required";
    }

    if (!formData.guardianMobileNumber.trim()) {
      newErrors.guardianMobileNumber = "Guardian mobile number is required";
    } else if (formData.guardianMobileNumber.trim().startsWith("+91")) {
      newErrors.guardianMobileNumber =
        "Please do not add country code (+91). Enter 10-digit mobile number only";
    } else if (!/^\+?[\d\s-()]{7,15}$/.test(formData.guardianMobileNumber)) {
      newErrors.guardianMobileNumber =
        "Please enter a valid guardian mobile number";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.joiningDate.trim()) {
      newErrors.joiningDate = "Joining date is required";
    }

    if (!formData.photo) {
      newErrors.photo = "Student photo is required";
    }

    const amountValue = parseFloat(formData.amount);
    const grantedDays = calculateGrantedDays(
      Number.isNaN(amountValue) ? 0 : amountValue
    );

    if (!formData.amount.trim()) {
      newErrors.amount = "Amount is required";
    } else if (Number.isNaN(amountValue) || amountValue <= MIN_AMOUNT) {
      newErrors.amount = `Amount must be greater than ₹${MIN_AMOUNT}`;
    } else if (grantedDays <= 0) {
      newErrors.amount = "Amount is too low to grant any days";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileUpload = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        photo: "Please select a valid image file",
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        photo: "Image size must be less than 5MB",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, photo: file }));
    setErrors((prev) => ({ ...prev, photo: "" }));

    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const startCamera = async (mode?: "user" | "environment") => {
    try {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }

      const cameraMode = mode || facingMode;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: cameraMode,
        },
      });
      setCameraStream(stream);
      setFacingMode(cameraMode);
      setShowCamera(true);
    } catch (error) {
      console.error("Error accessing camera:", error);
      setErrors((prev) => ({
        ...prev,
        photo: "Unable to access camera. Please check permissions.",
      }));
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const switchCamera = async () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    await startCamera(newMode);
  };

  const makePhotoName = () => `capture_${Date.now()}.jpg`;

  function dataUrlToBlob(dataURL: string): Blob | null {
    const parts = dataURL.split(",");
    if (parts.length !== 2) return null;
    const mimeMatch = parts[0].match(/data:(.*?);base64/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const byteString = atob(parts[1]);
    const len = byteString.length;
    const u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = byteString.charCodeAt(i);
    return new Blob([u8], { type: mime });
  }

  const capturePhoto = async () => {
    const video = document.getElementById(
      "camera-video"
    ) as HTMLVideoElement | null;
    if (!video) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = video.videoWidth || 0;
    const height = video.videoHeight || 0;
    if (!width || !height) return;

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(video, 0, 0, width, height);

    const previewDataURL = canvas.toDataURL("image/jpeg", 0.8);
    setPhotoPreview(previewDataURL);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.8)
    );

    if (!blob) {
      const dataURL = canvas.toDataURL("image/jpeg", 0.8);
      const fallbackBlob = dataUrlToBlob(dataURL);
      if (!fallbackBlob) return;
      const fallbackFile = new File([fallbackBlob], makePhotoName(), {
        type: "image/jpeg",
      });
      setFormData((prev) => ({ ...prev, photo: fallbackFile }));
      setErrors((prev) => ({ ...prev, photo: "" }));
      stopCamera();
      return;
    }

    const file = new File([blob], makePhotoName(), { type: "image/jpeg" });
    setFormData((prev) => ({ ...prev, photo: file }));
    setErrors((prev) => ({ ...prev, photo: "" }));
    stopCamera();
  };

  const removePhoto = () => {
    setFormData((prev) => ({ ...prev, photo: null }));
    setPhotoPreview(null);
    setErrors((prev) => ({ ...prev, photo: "" }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "aadharNumber") {
      newValue = value
        .replace(/\D/g, "")
        .slice(0, 12)
        .replace(/(\d{4})(?=\d)/g, "$1 ");
    } else if (
      name === "alternateNumber" ||
      name === "mobileNumber" ||
      name === "guardianMobileNumber"
    ) {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (errors[name as keyof StudentRegistrationFormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const amountValue = parseFloat(formData.amount);
  const grantedDays = calculateGrantedDays(
    Number.isNaN(amountValue) ? 0 : amountValue
  );
  const validityFrom = formData.joiningDate;
  const validityTo =
    grantedDays > 0 && validityFrom ? addDays(validityFrom, grantedDays) : "";
  const perDayRate = MONTHLY_AMOUNT / DAYS_PER_MONTH;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    const isValid = await validateForm();
    if (!isValid) return;

    if (!validityFrom || !validityTo) {
      setErrors({ general: "Unable to calculate validity dates." });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const orderResponse = await createRegistrationOrder({
        ...formData,
        validityFrom,
        validityTo,
      });
      if (orderResponse.success === false || !orderResponse.data) {
        throw new Error(
          orderResponse.message ||
            orderResponse.errors ||
            "Unable to start payment. Please try again."
        );
      }

      await processRazorpayPayment({
        existingOrder: orderResponse.data,
        purpose: "registration",
        description: "Registration Payment",
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.emailId,
          contact: formData.mobileNumber,
        },
      });

      setFormData(emptyForm(getTodayDate()));
      setPhotoPreview(null);
      setIsSubmitted(true);

      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("Registration error:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to register student. Please try again.";
      setErrors({
        general:
          message === "Payment cancelled."
            ? "Payment cancelled. Your registration was not saved. You can try again."
            : message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm(getTodayDate()));
    setErrors({});
    setIsSubmitted(false);
    setPhotoPreview(null);
    stopCamera();
  };

  return (
    <div className="student-registration">
      <div className="registration-header">
        <h2>Student Registration</h2>
        <p>Om Sai Reading Room — Register to join</p>
      </div>

      <div className="registration-card">
        {isSubmitted && (
          <div className="success-banner">
            <div className="success-icon">✓</div>
            <div className="success-content">
              <h4>Registration successful!</h4>
              <p>
                Payment was successful and your membership has been saved. Please
                contact Parthao Lad on 9405703380 for seat allotment.
              </p>
            </div>
          </div>
        )}

        {errors.general && (
          <div className="error-banner">
            <div className="error-icon">!</div>
            <div className="error-content">
              <h4>Registration Failed</h4>
              <p>{errors.general}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-header-section">
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? "error" : ""}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <span className="error-message">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? "error" : ""}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <span className="error-message">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="emailId">Email Address *</label>
                  <input
                    type="email"
                    id="emailId"
                    name="emailId"
                    value={formData.emailId}
                    onChange={handleInputChange}
                    className={errors.emailId ? "error" : ""}
                    placeholder="student@university.edu"
                    inputMode="email"
                    autoComplete="email"
                  />
                  {errors.emailId && (
                    <span className="error-message">{errors.emailId}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="mobileNumber">Mobile Number *</label>
                  <input
                    type="tel"
                    id="mobileNumber"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    className={errors.mobileNumber ? "error" : ""}
                    placeholder="10-digit mobile number"
                    inputMode="numeric"
                    autoComplete="tel"
                  />
                  {errors.mobileNumber && (
                    <span className="error-message">{errors.mobileNumber}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Joining Date</label>
                  <input
                    type="text"
                    value={formatDisplayDate(formData.joiningDate)}
                    readOnly
                    className="readonly-field"
                    disabled
                  />
                  <span className="field-note">
                    Set to today — not editable
                  </span>
                </div>

                <div className="form-group">
                  <label htmlFor="amount">
                    Amount (₹) <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="Enter amount"
                    min={MIN_AMOUNT + 1}
                    step="1"
                    className={errors.amount ? "error" : ""}
                    disabled={isSubmitting || isSubmitted}
                  />
                  {errors.amount && (
                    <span className="error-message">{errors.amount}</span>
                  )}
                  <span className="field-note">
                    Rate: ₹{MONTHLY_AMOUNT} = {DAYS_PER_MONTH} days (₹
                    {perDayRate.toFixed(2)}/day). Amount must be greater than ₹
                    {MIN_AMOUNT}.
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Granted Days</label>
                  <input
                    type="text"
                    value={
                      grantedDays > 0
                        ? `${grantedDays} day${grantedDays !== 1 ? "s" : ""}`
                        : `Enter amount above ₹${MIN_AMOUNT}`
                    }
                    readOnly
                    className="readonly-field"
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label>Validity To Date</label>
                  <input
                    type="text"
                    value={
                      validityTo
                        ? formatDisplayDate(validityTo)
                        : "Enter a valid amount"
                    }
                    readOnly
                    className="readonly-field"
                    disabled
                  />
                  <span className="field-note">
                    Auto-calculated from Joining Date + granted days — not
                    editable
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="aadharNumber">Aadhar Number *</label>
                  <input
                    type="text"
                    id="aadharNumber"
                    name="aadharNumber"
                    value={formData.aadharNumber}
                    onChange={handleInputChange}
                    className={errors.aadharNumber ? "error" : ""}
                    placeholder="1234 5678 9012"
                    maxLength={14}
                    inputMode="numeric"
                  />
                  {errors.aadharNumber && (
                    <span className="error-message">{errors.aadharNumber}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="genderCode">Gender *</label>
                  <select
                    id="genderCode"
                    name="genderCode"
                    value={formData.genderCode}
                    onChange={handleInputChange}
                    className={errors.genderCode ? "error" : ""}
                  >
                    <option value="">Select Gender</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other</option>
                  </select>
                  {errors.genderCode && (
                    <span className="error-message">{errors.genderCode}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="alternateNumber">
                    Alternate Mobile Number
                  </label>
                  <input
                    type="tel"
                    id="alternateNumber"
                    name="alternateNumber"
                    value={formData.alternateNumber}
                    onChange={handleInputChange}
                    className={errors.alternateNumber ? "error" : ""}
                    placeholder="Optional"
                    inputMode="numeric"
                  />
                  {errors.alternateNumber && (
                    <span className="error-message">
                      {errors.alternateNumber}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="optionalIdentification">Optional ID</label>
                  <input
                    type="text"
                    id="optionalIdentification"
                    name="optionalIdentification"
                    value={formData.optionalIdentification}
                    onChange={handleInputChange}
                    className={errors.optionalIdentification ? "error" : ""}
                    placeholder="Driver's License, Passport, etc. (Optional)"
                  />
                  {errors.optionalIdentification && (
                    <span className="error-message">
                      {errors.optionalIdentification}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="guardianName">Guardian Name *</label>
                  <input
                    type="text"
                    id="guardianName"
                    name="guardianName"
                    value={formData.guardianName}
                    onChange={handleInputChange}
                    className={errors.guardianName ? "error" : ""}
                    placeholder="Parent/Guardian Full Name"
                  />
                  {errors.guardianName && (
                    <span className="error-message">{errors.guardianName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="guardianMobileNumber">
                    Guardian Mobile Number *
                  </label>
                  <input
                    type="tel"
                    id="guardianMobileNumber"
                    name="guardianMobileNumber"
                    value={formData.guardianMobileNumber}
                    onChange={handleInputChange}
                    className={errors.guardianMobileNumber ? "error" : ""}
                    placeholder="10-digit mobile number"
                    inputMode="numeric"
                  />
                  {errors.guardianMobileNumber && (
                    <span className="error-message">
                      {errors.guardianMobileNumber}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address *</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={errors.address ? "error" : ""}
                  placeholder="Enter complete address"
                  rows={3}
                />
                {errors.address && (
                  <span className="error-message">{errors.address}</span>
                )}
              </div>
            </div>

            <div className="photo-section">
              <h3>Student Photo *</h3>
              <div className="photo-upload-section">
                {formData.photo ? (
                  <div className="photo-preview">
                    {photoPreview && (
                      <img
                        src={photoPreview}
                        alt="Student photo preview"
                        style={{
                          width: 120,
                          height: 120,
                          borderRadius: 8,
                          objectFit: "cover",
                        }}
                      />
                    )}
                    <div className="photo-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={removePhoto}
                      >
                        Remove Photo
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => startCamera()}
                      >
                        Take New Photo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`photo-upload-area ${
                      isDragOver ? "drag-over" : ""
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="upload-content">
                      <div className="upload-icon">
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M17 8L12 3L7 8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 3V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <h4>Upload Student Photo</h4>
                      <p>Drag and drop an image here, or tap to select</p>
                      <div className="upload-buttons">
                        <input
                          type="file"
                          id="photo-upload"
                          accept="image/*"
                          onChange={handleFileInputChange}
                          style={{ display: "none" }}
                        />
                        <label htmlFor="photo-upload" className="btn-secondary">
                          Choose File
                        </label>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => startCamera()}
                        >
                          Take Photo
                        </button>
                      </div>
                      <small className="upload-note">
                        Supported formats: JPG, PNG, GIF. Max size: 5MB
                      </small>
                    </div>
                  </div>
                )}
                {errors.photo && (
                  <span className="error-message">{errors.photo}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={resetForm}>
              Reset Form
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Opening payment...
                </>
              ) : (
                "Proceed to Payment"
              )}
            </button>
          </div>
        </form>
      </div>

      {showCamera && (
        <div className="camera-modal-overlay" onClick={stopCamera}>
          <div className="camera-modal" onClick={(e) => e.stopPropagation()}>
            <div className="camera-header">
              <h3>Take Student Photo</h3>
              <button
                type="button"
                className="close-camera"
                onClick={stopCamera}
                aria-label="Close camera"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="camera-content">
              <video
                id="camera-video"
                autoPlay
                playsInline
                muted
                ref={(video) => {
                  if (video && cameraStream) {
                    video.srcObject = cameraStream;
                  }
                }}
              />
              <div className="camera-controls">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={stopCamera}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={switchCamera}
                  title={
                    facingMode === "user"
                      ? "Switch to back camera"
                      : "Switch to front camera"
                  }
                >
                  {facingMode === "user" ? "Back Camera" : "Front Camera"}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={capturePhoto}
                >
                  Capture Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentRegistration;
